import {
  ref,
  computed,
} from 'vue';

const estaVacio = (valor) => valor === null || valor === undefined || valor === '';

/**
 * Progressive disclosure de los campos de un objeto: muestra solo los que tienen valor y deja
 * el resto disponible para agregar a demanda.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Object>} options.data - Objeto con los valores (ej. el material).
 * @param {import('vue').Ref<Array>} options.camposDisponibles - Diccionario `{ atributo, etiqueta, tipo }`.
 * @param {Object} [options.valueProp] - Atributos cuyo valor vive en otra propiedad (ej. Color → CodigoColor).
 * @param {string[]} [options.gestionados] - Propiedades que dependen del diccionario y se limpian
 *   cuando dejan de estar disponibles.
 */
export function useCamposDinamicos({ data, camposDisponibles, valueProp = {}, gestionados = [] }) {
  const visibleFields = ref([]);

  const propDe = (atributo) => valueProp[atributo] || atributo;

  const camposVisibles = computed(() =>
    visibleFields.value
      .map((atributo) => camposDisponibles.value.find((c) => c.atributo === atributo))
      .filter(Boolean),
  );

  const camposNulosDisponibles = computed(() =>
    camposDisponibles.value.filter((c) => !visibleFields.value.includes(c.atributo)),
  );

  /**
   * Fija los visibles según los valores actuales. Sólo se llama en la carga inicial y al cambiar
   * el tipo: no debe engancharse a los valores, o un campo recién vaciado desaparecería.
   */
  const initVisibleFields = () => {
    visibleFields.value = camposDisponibles.value
      .filter((c) => !estaVacio(data.value?.[propDe(c.atributo)]))
      .map((c) => c.atributo);
  };

  const agregarCampo = (atributo) => {
    if (!atributo || visibleFields.value.includes(atributo)) return;
    if (!camposDisponibles.value.some((c) => c.atributo === atributo)) return;
    visibleFields.value.push(atributo);
    const prop = propDe(atributo);
    if (data.value[prop] === undefined) data.value[prop] = null;
  };

  const vaciarCampo = (atributo) => {
    if (!visibleFields.value.includes(atributo)) return;
    data.value[propDe(atributo)] = null;
  };

  /** Anula los valores de los campos gestionados que el diccionario actual ya no admite. */
  const limpiarNoDisponibles = () => {
    const permitidos = new Set(camposDisponibles.value.map((c) => c.atributo));
    gestionados.forEach((atributo) => {
      if (!permitidos.has(atributo)) data.value[propDe(atributo)] = null;
    });
  };

  return {
    visibleFields,
    camposVisibles,
    camposNulosDisponibles,
    initVisibleFields,
    agregarCampo,
    vaciarCampo,
    limpiarNoDisponibles,
  };
}
